import { AnimatePresence, m } from "framer-motion";
import {
	cloneElement,
	type ReactElement,
	type ReactNode,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { createPortal } from "react-dom";
import "./Tooltip.css";

type Side = "top" | "bottom";

interface TooltipProps {
	content: ReactNode;
	children: ReactElement<{
		ref?: React.Ref<HTMLElement>;
		"aria-describedby"?: string;
		onMouseEnter?: (e: React.MouseEvent) => void;
		onMouseLeave?: (e: React.MouseEvent) => void;
		onFocus?: (e: React.FocusEvent) => void;
		onBlur?: (e: React.FocusEvent) => void;
		onClick?: (e: React.MouseEvent) => void;
	}>;
	preferredSide?: Side;
	maxWidth?: number;
}

interface Position {
	top: number;
	left: number;
	side: Side;
}

const GAP = 8;
const EDGE_PAD = 8;

function isTouchDevice() {
	if (typeof window === "undefined") return false;
	return window.matchMedia?.("(pointer: coarse)").matches ?? false;
}

export function Tooltip({
	content,
	children,
	preferredSide = "bottom",
	maxWidth = 280,
}: TooltipProps) {
	const id = useId();
	const triggerRef = useRef<HTMLElement | null>(null);
	const tooltipRef = useRef<HTMLDivElement | null>(null);
	const [open, setOpen] = useState(false);
	const [position, setPosition] = useState<Position | null>(null);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	const computePosition = useCallback(() => {
		const trigger = triggerRef.current;
		const tooltip = tooltipRef.current;
		if (!trigger || !tooltip) return;
		const tRect = trigger.getBoundingClientRect();
		const tipRect = tooltip.getBoundingClientRect();
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		let side: Side = preferredSide;
		const spaceBelow = vh - tRect.bottom;
		const spaceAbove = tRect.top;
		if (
			side === "bottom" &&
			spaceBelow < tipRect.height + GAP + EDGE_PAD &&
			spaceAbove > spaceBelow
		) {
			side = "top";
		} else if (
			side === "top" &&
			spaceAbove < tipRect.height + GAP + EDGE_PAD &&
			spaceBelow > spaceAbove
		) {
			side = "bottom";
		}

		const top =
			side === "bottom"
				? tRect.bottom + GAP + window.scrollY
				: tRect.top - tipRect.height - GAP + window.scrollY;

		let left =
			tRect.left + tRect.width / 2 - tipRect.width / 2 + window.scrollX;
		const minLeft = window.scrollX + EDGE_PAD;
		const maxLeft = window.scrollX + vw - tipRect.width - EDGE_PAD;
		if (left < minLeft) left = minLeft;
		if (left > maxLeft) left = maxLeft;

		setPosition({ top, left, side });
	}, [preferredSide]);

	useLayoutEffect(() => {
		if (!open) return;
		computePosition();
		const onScrollOrResize = () => computePosition();
		window.addEventListener("scroll", onScrollOrResize, true);
		window.addEventListener("resize", onScrollOrResize);
		return () => {
			window.removeEventListener("scroll", onScrollOrResize, true);
			window.removeEventListener("resize", onScrollOrResize);
		};
	}, [open, computePosition]);

	useEffect(() => {
		if (!open) return;
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false);
		};
		document.addEventListener("keydown", handleKey);
		return () => document.removeEventListener("keydown", handleKey);
	}, [open]);

	useEffect(() => {
		if (!open || !isTouchDevice()) return;
		const handlePointer = (e: PointerEvent) => {
			const target = e.target as Node | null;
			if (
				target &&
				!triggerRef.current?.contains(target) &&
				!tooltipRef.current?.contains(target)
			) {
				setOpen(false);
			}
		};
		document.addEventListener("pointerdown", handlePointer);
		return () => document.removeEventListener("pointerdown", handlePointer);
	}, [open]);

	const childProps = children.props;
	const isFocusInsideTooltipGroup = (target: EventTarget | null): boolean => {
		if (!(target instanceof Node)) return false;
		return Boolean(
			triggerRef.current?.contains(target) ||
				tooltipRef.current?.contains(target),
		);
	};

	const trigger = cloneElement(children, {
		ref: (node: HTMLElement | null) => {
			triggerRef.current = node;
		},
		"aria-describedby": open ? id : undefined,
		onMouseEnter: (e: React.MouseEvent) => {
			childProps.onMouseEnter?.(e);
			if (!isTouchDevice()) setOpen(true);
		},
		onMouseLeave: (e: React.MouseEvent) => {
			childProps.onMouseLeave?.(e);
			if (!isTouchDevice()) setOpen(false);
		},
		onFocus: (e: React.FocusEvent) => {
			childProps.onFocus?.(e);
			setOpen(true);
		},
		onBlur: (e: React.FocusEvent) => {
			childProps.onBlur?.(e);
			// Keep the tooltip open when focus moves into its own content
			// (e.g. the learn-more link). Closing on plain blur would unmount
			// the link before keyboard users can reach it.
			if (isFocusInsideTooltipGroup(e.relatedTarget)) return;
			setOpen(false);
		},
		onClick: (e: React.MouseEvent) => {
			childProps.onClick?.(e);
			if (isTouchDevice()) setOpen((prev) => !prev);
		},
	});

	const portalTarget =
		mounted && typeof document !== "undefined" ? document.body : null;

	return (
		<>
			{trigger}
			{portalTarget &&
				createPortal(
					<AnimatePresence>
						{open && (
							<m.div
								ref={tooltipRef}
								id={id}
								role="tooltip"
								className="ui-tooltip"
								style={{
									top: position?.top ?? -9999,
									left: position?.left ?? -9999,
									maxWidth,
									visibility: position ? "visible" : "hidden",
								}}
								data-side={position?.side ?? preferredSide}
								initial={{ opacity: 0, y: position?.side === "top" ? 4 : -4 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: position?.side === "top" ? 4 : -4 }}
								transition={{ duration: 0.14, ease: [0.25, 0.46, 0.45, 0.94] }}
								onBlur={(e) => {
									// Close when focus leaves the tooltip altogether
									// (and isn't returning to the trigger).
									if (isFocusInsideTooltipGroup(e.relatedTarget)) return;
									setOpen(false);
								}}
							>
								{content}
							</m.div>
						)}
					</AnimatePresence>,
					portalTarget,
				)}
		</>
	);
}
